import { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { MapPin, Home, Landmark, Plane, ShoppingBag, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';

const AutocompleteInput = ({ value, onChange, onSelect, placeholder, className }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const debounceTimerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (value && value.length >= 2) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        fetchSuggestions(value);
      }, 300);
    } else {
      setSuggestions([]);
      setIsOpen(false);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [value]);

  const fetchSuggestions = async (query) => {
    setLoading(true);
    try {
      const response = await api.get('/search/autocomplete', {
        params: { q: query }
      });
      setSuggestions(response.data.suggestions || []);
      setIsOpen(true);
      setSelectedIndex(-1);
    } catch (error) {
      console.error('Autocomplete error:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (type, iconName) => {
    const iconProps = { className: "w-4 h-4 text-muted-foreground" };
    
    if (iconName === 'map-pin' || type === 'location') return <MapPin {...iconProps} />;
    if (iconName === 'home' || type === 'property') return <Home {...iconProps} />;
    if (iconName === 'landmark' || type === 'poi') return <Landmark {...iconProps} />;
    if (iconName === 'plane') return <Plane {...iconProps} />;
    if (iconName === 'shopping-bag') return <ShoppingBag {...iconProps} />;
    
    return <MapPin {...iconProps} />;
  };

  const handleSelect = (suggestion) => {
    if (suggestion.type === 'property') {
      if (onSelect) onSelect(suggestion);
      onChange(suggestion.name);
    } else {
      onChange(suggestion.name);
    }
    setIsOpen(false);
    setSuggestions([]);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => 
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0 && suggestions[selectedIndex]) {
        handleSelect(suggestions[selectedIndex]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setSelectedIndex(-1);
    }
  };

  return (
    <div ref={wrapperRef} className="relative w-full">
      <Input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => suggestions.length > 0 && setIsOpen(true)}
        className={className}
        data-testid="autocomplete-input"
      />
      
      {loading && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        </div>
      )}

      {isOpen && suggestions.length > 0 && (
        <div 
          className="absolute z-50 w-full mt-2 bg-white border rounded-lg shadow-lg max-h-80 overflow-y-auto"
          data-testid="autocomplete-dropdown"
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              onClick={() => handleSelect(suggestion)}
              className={`w-full px-4 py-3 text-left hover:bg-accent transition-colors flex items-center gap-3 ${
                index === selectedIndex ? 'bg-accent' : ''
              }`}
              data-testid={`autocomplete-suggestion-${index}`}
            >
              {getIcon(suggestion.type, suggestion.icon)}
              <div className="flex-1">
                <div className="font-medium text-sm">{suggestion.name}</div>
                {suggestion.location && (
                  <div className="text-xs text-muted-foreground">{suggestion.location}</div>
                )}
                {suggestion.price && (
                  <div className="text-xs text-secondary font-semibold mt-0.5">
                    ${suggestion.price}/noite
                  </div>
                )}
              </div>
              <div className="text-xs text-muted-foreground capitalize">
                {suggestion.type === 'location' && 'Local'}
                {suggestion.type === 'property' && 'Imóvel'}
                {suggestion.type === 'poi' && 'Ponto de Interesse'}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AutocompleteInput;
